import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { UserPlus, ArrowLeft, Shield, Building2, School, Trophy, Loader2, CheckCircle } from 'lucide-react';
import MaraSportsLogo from '../components/MaraSportsLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
    { value: 'federation_admin', label: 'Federation Admin', desc: 'Manage a specific sport federation', icon: Trophy },
    { value: 'club_admin', label: 'Club Admin', desc: 'Manage a sports club', icon: Shield },
    { value: 'school_admin', label: 'School Admin', desc: 'Manage school sports programs', icon: School },
    { value: 'nock_admin', label: 'NOCK Admin', desc: 'National Olympic Committee', icon: Building2 },
    { value: 'education_admin', label: 'Ministry of Education', desc: 'Oversee all school sports', icon: Building2 },
    { value: 'ministry_admin', label: 'Ministry of Sports', desc: 'National sports oversight', icon: Building2 },
];

const SPORTS = [
    "Athletics Track and Field", "Basketball", "Hockey", "Football", "Volleyball",
    "Rugby", "Netball", "Chess", "Table Tennis", "Karate", "Taekwondo",
    "Badminton", "Swimming", "Tennis", "Handball", "Judo", "Padel",
    "Squash", "E-Sports", "Motor Racing"
];

const COUNTIES = [
    "Nairobi", "Mombasa", "Kisumu", "Nakuru", "Kiambu", "Uasin Gishu",
    "Machakos", "Kajiado", "Kilifi", "Kwale", "Nyeri", "Murang'a",
    "Bungoma", "Kakamega", "Trans Nzoia", "Nandi", "Kericho"
];

const AdminRegisterPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState({
        full_name: '',
        email: '',
        password: '',
        confirm_password: '',
        phone: '',
        requested_role: '',
        organization_name: '',
        organization_type: '',
        sport: '',
        county: '',
        reason: '',
    });

    const needsSport = ['federation_admin', 'club_admin'].includes(form.requested_role);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (form.password !== form.confirm_password) {
            toast.error('Passwords do not match');
            return;
        }
        if (form.password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        if (!form.requested_role) {
            toast.error('Please select a role');
            return;
        }
        if (!form.organization_name.trim()) {
            toast.error('Please enter your organization name');
            return;
        }
        if (!form.phone.trim()) {
            toast.error('Please enter your phone number');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                full_name: form.full_name,
                email: form.email,
                password: form.password,
                phone: form.phone,
                requested_role: form.requested_role,
                organization_name: form.organization_name,
                organization_type: form.requested_role.replace('_admin', ''),
                sport: needsSport ? form.sport : null,
                county: form.county || null,
                reason: form.reason || null,
            };
            await axios.post(`${API_URL}/api/auth/admin-register`, payload);
            setSubmitted(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#0a1628] flex items-center justify-center p-4">
                <div className="w-full max-w-md text-center space-y-6" data-testid="admin-register-success">
                    <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto" />
                    <h1 className="text-2xl font-bold">Application Submitted</h1>
                    <p className="text-muted-foreground">
                        Your admin account application has been submitted for review. 
                        The Master Admin will review and approve your application.
                    </p>
                    <p className="text-sm text-muted-foreground">
                        You will be able to log in once your application is approved.
                    </p>
                    <Button onClick={() => navigate('/admin/login')} className="mt-4" data-testid="back-to-login-btn">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Admin Login
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a1628] flex items-center justify-center p-4">
            <div className="w-full max-w-2xl">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <MaraSportsLogo size={40} />
                        <span className="text-xl font-bold tracking-tight">NEXTGEN <span className="text-primary">TOURNAMENTS</span></span>
                    </div>
                    <h1 className="text-2xl font-bold flex items-center justify-center gap-2">
                        <UserPlus className="w-6 h-6 text-primary" />
                        Admin Registration
                    </h1>
                    <p className="text-sm text-muted-foreground mt-2">
                        Apply for an admin account. Your application will be reviewed by the Master Admin.
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-[#0d2847] border border-white/10 rounded-lg p-6 space-y-5" data-testid="admin-register-form">
                    {/* Personal Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Full Name *</Label>
                            <Input
                                required
                                value={form.full_name}
                                onChange={e => setForm({...form, full_name: e.target.value})}
                                placeholder="John Doe"
                                className="bg-[#0a1628] border-white/10"
                                data-testid="register-full-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Email *</Label>
                            <Input
                                type="email"
                                required
                                value={form.email}
                                onChange={e => setForm({...form, email: e.target.value})}
                                placeholder="admin@organization.co.ke"
                                className="bg-[#0a1628] border-white/10"
                                data-testid="register-email"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Password *</Label>
                            <Input
                                type="password"
                                required
                                value={form.password}
                                onChange={e => setForm({...form, password: e.target.value})}
                                placeholder="Min 6 characters"
                                className="bg-[#0a1628] border-white/10"
                                data-testid="register-password"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Confirm Password *</Label>
                            <Input
                                type="password"
                                required
                                value={form.confirm_password}
                                onChange={e => setForm({...form, confirm_password: e.target.value})}
                                placeholder="Repeat password"
                                className="bg-[#0a1628] border-white/10"
                                data-testid="register-confirm-password"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Phone *</Label>
                        <Input
                            required
                            value={form.phone}
                            onChange={e => setForm({...form, phone: e.target.value})}
                            placeholder="+254 7XX XXX XXX"
                            className="bg-[#0a1628] border-white/10"
                            data-testid="register-phone"
                        />
                    </div>

                    {/* Role Selection */}
                    <div className="space-y-3">
                        <Label>Requested Admin Role *</Label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {ROLES.map(role => {
                                const Icon = role.icon;
                                const selected = form.requested_role === role.value;
                                return (
                                    <div
                                        key={role.value}
                                        onClick={() => setForm({...form, requested_role: role.value})}
                                        className={`border rounded-lg p-3 cursor-pointer transition-all ${
                                            selected
                                                ? 'border-primary bg-primary/10'
                                                : 'border-white/10 hover:border-white/30'
                                        }`}
                                        data-testid={`role-${role.value}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Icon className={`w-4 h-4 ${selected ? 'text-primary' : 'text-muted-foreground'}`} />
                                            <span className={`text-sm font-medium ${selected ? 'text-primary' : ''}`}>{role.label}</span>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">{role.desc}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Organization Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Organization Name *</Label>
                            <Input
                                required
                                value={form.organization_name}
                                onChange={e => setForm({...form, organization_name: e.target.value})}
                                placeholder={
                                    form.requested_role === 'school_admin' ? "e.g., St. Mary's School Nairobi" :
                                    form.requested_role === 'federation_admin' ? "e.g., Kenya Football Federation" :
                                    form.requested_role === 'club_admin' ? "e.g., Gor Mahia FC" :
                                    "Organization name"
                                }
                                className="bg-[#0a1628] border-white/10"
                                data-testid="register-org-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>County</Label>
                            <Select value={form.county} onValueChange={v => setForm({...form, county: v})}>
                                <SelectTrigger className="bg-[#0a1628] border-white/10" data-testid="register-county">
                                    <SelectValue placeholder="Select county" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0d2847] border-white/10">
                                    {COUNTIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Sport selection for federation/club */}
                    {needsSport && (
                        <div className="space-y-2">
                            <Label>Primary Sport *</Label>
                            <Select value={form.sport} onValueChange={v => setForm({...form, sport: v})}>
                                <SelectTrigger className="bg-[#0a1628] border-white/10" data-testid="register-sport">
                                    <SelectValue placeholder="Select sport" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0d2847] border-white/10">
                                    {SPORTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Reason */}
                    <div className="space-y-2">
                        <Label>Why should you be approved?</Label>
                        <Textarea
                            value={form.reason}
                            onChange={e => setForm({...form, reason: e.target.value})}
                            placeholder="Explain your role and why you need admin access..."
                            className="bg-[#0a1628] border-white/10 min-h-[80px]"
                            data-testid="register-reason"
                        />
                    </div>

                    {/* Submit */}
                    <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-btn">
                        {loading ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                        ) : (
                            <><UserPlus className="w-4 h-4 mr-2" /> Submit Application</>
                        )}
                    </Button>

                    <div className="text-center text-sm text-muted-foreground">
                        Already have an account?{' '}
                        <Link to="/admin/login" className="text-primary hover:underline">Log in here</Link>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminRegisterPage;
